// cesium-styling.ts - Manages asset styling
//
// Uses JavaScript-based evaluation instead of string expressions to prevent crashes
// This approach is more robust and handles missing properties gracefully

import {
  Cesium3DTileColorBlendMode,
  type Cesium3DTileFeature,
  Cesium3DTileStyle,
  type Cesium3DTileset,
  Color,
  type Viewer,
} from "cesium";
import bimStyleConditions from "./bim-style-conditions-2025-09-03.json";
import type { StyleCondition } from "./cesium-types";

// Type for the BIM style conditions JSON

// Helper functions for safe color parsing
const clamp01 = (n: number | undefined) =>
  Math.max(0, Math.min(1, Number(n) || 1));

const parseCssColor = (css: string, alpha?: number): Color => {
  try {
    const color = Color.fromCssColorString(css);
    if (color && Number.isFinite(alpha)) {
      color.alpha = clamp01(alpha);
    }
    return color || Color.WHITE;
  } catch {
    return Color.WHITE;
  }
};

// Apply a set of style conditions to a tileset using safe JavaScript-based evaluation
export function applyStylesToTileset(
  viewer: Viewer,
  tileset: Cesium3DTileset,
  overrideStyles: StyleCondition[] = [],
  applyBaseStyle = true,
) {
  if (!viewer || !tileset) {
    return;
  }

  // console.log(
  //   "Override styles being applied:",
  //   overrideStyles,
  //   bimStyleConditions,
  // );

  const allStyles =
    overrideStyles.length > 0
      ? [...overrideStyles, ...bimStyleConditions]
      : bimStyleConditions;

  try {
    // Build rules array for evaluation
    const rules: Array<{
      test: (feature: Cesium3DTileFeature) => boolean;
      color: () => Color;
    }> = [];

    // Add override rules first (highest priority after highlighting)
    for (const style of allStyles) {
      if (!style?.property || style.color == null) continue;

      rules.push({
        test: (feature) => {
          try {
            return feature?.getProperty?.(style.property) === style.value;
          } catch {
            return false;
          }
        },
        color: () => parseCssColor(style.color, style.opacity),
      });
    }

    // Add base style rules
    // if (applyBaseStyle) {
    //   for (const [propertyName, valueColorMap] of Object.entries(
    //     ifcClassColorMap,
    //   )) {
    //     for (const [value, colorData] of Object.entries(valueColorMap)) {
    //       // Skip completely transparent elements (opacity 0)
    //       if (typeof colorData === "object" && colorData.opacity === 0) {
    //         console.log(
    //           `Skipping transparent element: ${propertyName}.${value} (opacity: 0)`,
    //         );
    //         continue;
    //       }

    //       rules.push({
    //         test: (feature) => {
    //           try {
    //             return feature?.getProperty?.(propertyName) === value;
    //           } catch {
    //             return false;
    //           }
    //         },
    //         color: () => parseCssColor(colorData.color, colorData.opacity),
    //       });
    //     }
    //   }
    // }

    // Create style with safe JavaScript-based evaluation
    const style = new Cesium3DTileStyle({
      color: {
        evaluateColor: (feature: Cesium3DTileFeature, result: Color) => {
          try {
            // Check for highlighting first
            if (feature?.getProperty?.("_isHighlighted") === true) {
              return Color.clone(Color.YELLOW, result);
            }

            // Find first matching rule
            for (const rule of rules) {
              if (rule.test(feature)) {
                return Color.clone(rule.color(), result);
              }
            }

            // Default fallback
            return Color.clone(Color.WHITE, result);
          } catch {
            // Ultimate fallback if anything goes wrong
            return Color.clone(Color.WHITE, result);
          }
        },
      },
      show: {
        evaluate: (feature: Cesium3DTileFeature) => {
          try {
            // Use the same allStyles array as color evaluation
            for (const style of allStyles) {
              if (
                style?.property &&
                feature?.getProperty?.(style.property) === style.value
              ) {
                const shouldShow = style.opacity > 0;
                return shouldShow;
              }
            }
            return true; // Show by default
          } catch (error) {
            console.log("Error in show evaluation:", error);
            return true; // Show by default if error
          }
        },
      },
    });

    tileset.colorBlendMode = Cesium3DTileColorBlendMode.REPLACE;
    tileset.style = style;
    viewer.scene.requestRender();
  } catch (error) {
    console.error("Error applying styles to tileset:", error);
    // Fallback to a simple white style if styling fails
    try {
      tileset.style = new Cesium3DTileStyle({
        color: "color('white')",
        show: true,
      });
      tileset.colorBlendMode = Cesium3DTileColorBlendMode.REPLACE;
      viewer.scene.requestRender();
    } catch (fallbackError) {
      console.error("Failed to apply fallback style:", fallbackError);
    }
  }
}

// Apply styles to all registered tilesets, now asynchronous
export function applyStylesToAllTilesets(
  viewer: Viewer,
  tilesetRegistry: Map<string | number, Cesium3DTileset>,
  styleConditions: StyleCondition[],
): void {
  if (!viewer) {
    return;
  }

  try {
    for (const tileset of tilesetRegistry.values()) {
      applyStylesToTileset(viewer, tileset, styleConditions);
    }
  } catch (error) {
    console.error("Error applying styles to a tileset:", error);
    // rethrow or handle, here we rethrow so the caller's catch can handle it
    throw error;
  } finally {
    viewer.scene.requestRender();
  }
}

// Function to apply initial styles after tileset loading
export function applyInitialStyles(
  viewer: Viewer,
  tilesetRegistry: Map<string | number, Cesium3DTileset>,
  bimAssetStyles?: StyleCondition[],
) {
  if (!viewer) {
    console.log("No viewer, returning early");
    return;
  }

  const readyPromises = Array.from(tilesetRegistry.values()).map(
    // @ts-expect-error readyPromise is an internal Cesium property
    (tileset) => tileset.readyPromise,
  );

  Promise.allSettled(readyPromises).then((results) => {
    results.forEach((result) => {
      if (result.status === "rejected") {
        console.warn("A tileset failed to load:", result.reason);
      }
    });

    // Apply styles once all tilesets have settled (either loaded or failed).
    try {
      setTimeout(() => {
        // Always apply base styles (JSON-based styling) to all tilesets
        for (const tileset of tilesetRegistry.values()) {
          applyStylesToTileset(viewer, tileset, [], true); // Empty override styles, but applyBaseStyle = true
        }

        // Also apply any custom bimAssetStyles if they exist
        if (bimAssetStyles && bimAssetStyles.length > 0) {
          applyStylesToAllTilesets(viewer, tilesetRegistry, bimAssetStyles);
        }
      }, 1000);
    } catch (error) {
      console.error("Error applying styles to tilesets:", error);
    }
  });
}
